import { useState } from 'react';
import type { Lang } from '../page';

interface PurchaseSupportProps { lang: Lang; }

type LM = Record<Lang, string>;

const readyLabel: LM = { en: 'READY TO ORDER', zh: '准备订购', th: 'พร้อมสั่งซื้อ', vi: 'SẴN SÀNG ĐẶT HÀNG', ms: 'SEDIA UNTUK MEMESAN' };
const startLabel: LM = { en: 'Start Your', zh: '开启您的', th: 'เริ่มต้น', vi: 'Bắt Đầu', ms: 'Mulakan' };
const partnerLabel: LM = { en: 'Partnership', zh: '合作伙伴', th: 'ความร่วมมือ', vi: 'Hợp Tác', ms: 'Perkongsian' };
const todayLabel: LM = { en: 'Today', zh: '之旅', th: 'วันนี้', vi: 'Ngay Hôm Nay', ms: 'Hari Ini' };
const heading: LM = { en: 'Get Custom Quote', zh: '获取专属报价', th: 'รับใบเสนอราคาเฉพาะ', vi: 'Nhận Báo Giá Tùy Chỉnh', ms: 'Dapatkan Sebut Harga Khusus' };
const headingSub: LM = { en: '获取专属报价', zh: 'Get Custom Quote', th: 'Get Custom Quote', vi: 'Nhận báo giá', ms: 'Sebut harga khusus' };
const formDesc: LM = {
  en: 'Fill in your requirements and our professional team will contact you within 24 hours to provide customized solutions and competitive pricing.',
  zh: '填写您的需求，我们的专业团队将在24小时内与您联系，提供定制解决方案和具竞争力的报价。',
  th: 'กรอกข้อมูลความต้องการของคุณ ทีมงานมืออาชีพของเราจะติดต่อคุณภายใน 24 ชั่วโมง',
  vi: 'Điền yêu cầu của bạn và đội ngũ chuyên nghiệp của chúng tôi sẽ liên hệ trong vòng 24 giờ.',
  ms: 'Isi keperluan anda dan pasukan profesional kami akan menghubungi anda dalam masa 24 jam.',
};
const companyPH: LM = { en: 'Company Name', zh: '公司名称', th: 'ชื่อบริษัท', vi: 'Tên Công Ty', ms: 'Nama Syarikat' };
const contactPH: LM = { en: 'Contact (Phone/Email)', zh: '联系方式（电话/邮件）', th: 'ผู้ติดต่อ (โทรศัพท์/อีเมล)', vi: 'Liên hệ (Điện thoại/Email)', ms: 'Kenalan (Telefon/E-mel)' };
const submittingLabel: LM = { en: 'Submitting...', zh: '提交中...', th: 'กำลังส่ง...', vi: 'Đang Gửi...', ms: 'Menghantar...' };
const submitBtn: LM = { en: 'Submit Request', zh: '提交需求', th: 'ส่งคำขอ', vi: 'Gửi Yêu Cầu', ms: 'Hantar Permintaan' };
const successMsg: LM = {
  en: 'Submitted successfully! We will contact you soon.',
  zh: '提交成功！我们将尽快与您联系。',
  th: 'ส่งสำเร็จ! เราจะติดต่อคุณเร็วๆ นี้',
  vi: 'Đã gửi thành công! Chúng tôi sẽ liên hệ sớm.',
  ms: 'Berjaya dihantar! Kami akan menghubungi anda tidak lama lagi.',
};
const onlineSupport: LM = { en: 'Online Support', zh: '在线支持', th: 'สนับสนุนออนไลน์', vi: 'Hỗ Trợ Trực Tuyến', ms: 'Sokongan Dalam Talian' };
const datasheet: LM = { en: 'Datasheet', zh: '数据手册', th: 'เอกสารข้อมูล', vi: 'Tài Liệu Kỹ Thuật', ms: 'Helaian Data' };
const qualityLabel: LM = { en: 'Quality Assured', zh: '品质保障', th: 'รับประกันคุณภาพ', vi: 'Đảm Bảo Chất Lượng', ms: 'Jaminan Kualiti' };

export default function PurchaseSupport({ lang }: PurchaseSupportProps) {
  const [formData, setFormData] = useState({ company: '', contact: '', quantity: '100-500kg' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }, 1500);
  };

  return (
    <section id="purchase" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2">
          <div className="relative bg-amber-50 p-12 lg:p-16 flex flex-col justify-between min-h-[600px]">
            <div>
              <div className="text-sm font-medium text-gray-600 tracking-wider mb-8">{readyLabel[lang]}</div>
              <div className="space-y-2">
                <div className="text-4xl font-bold text-gray-900">{startLabel[lang]}</div>
                <div className="text-6xl font-bold text-gray-900">{partnerLabel[lang]}</div>
                <div className="text-7xl font-black text-gray-900">{todayLabel[lang]}</div>
              </div>
            </div>
            <div className="mt-12">
              <img
                src="https://static.readdy.ai/image/c1d369de78ba6b6a884b69ca21dbc9f2/f5efcb423912c716745ef48665b736c5.png"
                alt="Product Packaging"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>

          <div className="bg-white p-12 lg:p-16 flex flex-col justify-center">
            <div className="max-w-md mx-auto w-full">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 text-center mb-2">{heading[lang]}</h2>
              <p className="text-xl text-gray-600 text-center mb-4">{headingSub[lang]}</p>
              <p className="text-gray-600 text-center mb-12 leading-relaxed">{formDesc[lang]}</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <input
                  type="text"
                  placeholder={companyPH[lang]}
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-6 py-4 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 transition-colors"
                  required
                />
                <input
                  type="text"
                  placeholder={contactPH[lang]}
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  className="w-full px-6 py-4 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 transition-colors"
                  required
                />
                <select
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  className="w-full px-6 py-4 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-amber-500 transition-colors bg-white"
                >
                  <option value="100-500kg">100-500kg</option>
                  <option value="500-1000kg">500-1000kg</option>
                  <option value="1000-5000kg">1000-5000kg</option>
                  <option value="5000kg+">5000kg+</option>
                </select>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center space-x-2 px-8 py-5 bg-gray-900 text-white rounded-full font-semibold hover:bg-gray-800 transition-all disabled:opacity-50 whitespace-nowrap"
                >
                  {isSubmitting ? <span>{submittingLabel[lang]}</span> : (
                    <><span>{submitBtn[lang]}</span><i className="ri-arrow-right-line"></i></>
                  )}
                </button>
                {submitStatus === 'success' && (
                  <div className="text-center text-green-600 font-medium">{successMsg[lang]}</div>
                )}
              </form>

              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <i className="ri-customer-service-2-line text-3xl text-amber-500 mb-2"></i>
                    <div className="text-xs text-gray-600">{onlineSupport[lang]}</div>
                  </div>
                  <div>
                    <i className="ri-download-cloud-line text-3xl text-amber-500 mb-2"></i>
                    <div className="text-xs text-gray-600">{datasheet[lang]}</div>
                  </div>
                  <div>
                    <i className="ri-shield-check-line text-3xl text-amber-500 mb-2"></i>
                    <div className="text-xs text-gray-600">{qualityLabel[lang]}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
